// Copyright (c) 2019, 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import { comparerByField } from "../util";

export const SCHEMA_V1 = 1;

export type Uid = string;

export enum ReviewState {
  Approved = "approved",
  Pending = "pending",
  Rejected = "rejected",
}

export interface HasUid {
  uid: Uid;
}

export interface HasName {
  name: string;
}

export interface HasEmail {
  email: string;
}

export interface HasSchool {
  schoolUid: Uid;
}

export interface HasVolunteer {
  volunteerUid: Uid;
}

export interface ActivityData extends HasUid, HasSchool {
  uid: Uid;
  schoolUid: Uid;
  startDate: string;
  endDate: string;
  durationInSec: number;
  description: string;
  categoryUid: Uid;
  organizationUid: Uid;
  supervisorUid: Uid;
  isDeleted?: boolean;
}

// Extracts just ActivityData from a context that could include more
export function activityData(original: ActivityData): ActivityData {
  const { uid, schoolUid, startDate, endDate, durationInSec, description, categoryUid, organizationUid, supervisorUid } = original;
  const data = { uid, schoolUid, startDate, endDate, durationInSec, description, categoryUid, organizationUid, supervisorUid };
  if (original.isDeleted == null) {
    return data;
  } else {
    return { ...data, isDeleted: original.isDeleted };
  }
}

export interface Activity extends ActivityData, HasVolunteer {
  volunteerUid: Uid;
}

export interface ActivityImport extends ActivityData {
  volunteerEmail: string;
}

// uid is same as corresponding Activity.
export interface ActivityReview extends HasUid, HasSchool {
  uid: Uid;
  schoolUid: Uid;
  state: ReviewState;

  // Additional context about where this came from.
  // (for imports, this is the ImportRow, for activities this is the original ActivityDocument.)
  contents: any;
}

export interface Administrator extends HasName, HasEmail {
  uid: Uid;
  name: string;
  email: string;
}

export interface Category extends HasUid, HasName, HasSchool {
  uid: Uid;
  schoolUid: Uid;
  name: string;
  sortOverride?: number;
}

export interface Coordinator extends HasUid, HasName, HasEmail, HasSchool {
  uid: Uid;
  schoolUid: Uid;
  name: string;
  email: string;
  phone: string;
}

export interface Markdown extends HasUid, HasSchool, HasName {
  uid: Uid;
  schoolUid: Uid;
  name: string;
  title: string;
  contents: string;
}

export interface OrganizationData extends HasUid, HasSchool, HasName {
  uid: Uid;
  schoolUid: Uid;
  name: string;
  categoryUid: string;
  isDeleted?: boolean;
}

// Extracts just OrganizationData from a context that could include more
export function organizationData(original: OrganizationData): OrganizationData {
  const { uid, schoolUid, name, categoryUid, isDeleted } = original;
  const data = { uid, schoolUid, name, categoryUid, isDeleted };
  if (original.isDeleted == null) {
    return data;
  } else {
    return { ...data, isDeleted: original.isDeleted };
  }
}

export interface Organization extends OrganizationData, HasVolunteer {
  volunteerUid: Uid;
}

export interface OrganizationImport extends OrganizationData {
  volunteerEmail: string;
}

export interface School extends HasUid, HasName {
  uid: Uid;
  name: string;
  coordinatorEmail?: string;
  primaryColor?: string;
  accentColor?: string;
  requiredMarkdowns: string[];
}

export interface SupervisorData extends HasUid, HasSchool, HasName, HasEmail {
  uid: Uid;
  schoolUid: Uid;
  name: string;
  phone: string;
  email: string;
  organizationUid: Uid;
  isDeleted?: boolean;
}

export function supervisorData(original: SupervisorData): SupervisorData {
  const { uid, schoolUid, name, phone, email, organizationUid } = original;
  const data = { uid, schoolUid, name, phone, email, organizationUid };
  if (original.isDeleted == null) {
    return data;
  } else {
    return { ...data, isDeleted: original.isDeleted };
  }
}

export interface Supervisor extends SupervisorData, HasVolunteer {
  volunteerUid: Uid;
}

export interface SupervisorImport extends SupervisorData {
  volunteerEmail: string;
}

export interface AcceptedMarkdown extends HasUid, HasVolunteer {
  uid: Uid;
  volunteerUid: Uid;
  markdownUid: Uid;
  date: string;
}

export interface CoordinatorRole {
  schoolUid: Uid;
}

export interface UserInfo extends HasEmail {
  email: string;
  allowUnverified: boolean;
  coordinatorRole?: CoordinatorRole;
}

export interface Volunteer extends HasUid, HasName, HasEmail {
  uid: Uid;
  name: string;
  email: string;
  phone: string;
  preferredName?: string;
  classYear: number;
  schoolUid: Uid;
  acceptedMarkdowns: AcceptedMarkdown[];
}

export interface VolunteerImport extends HasName, HasEmail {
  name: string;
  email: string;
  classYear: number;
  schoolUid: Uid;
  activities: ActivityImport[];
  organizations: OrganizationImport[];
  supervisors: SupervisorImport[];
  importedFrom: any[];
}

export enum DocType {
  Activity = "activity",
  ActivityReview = "activity-review",
  Administrator = "administrator",
  Category = "category",
  Coordinator = "coordinator",
  Markdown = "markdown",
  Organization = "organization",
  School = "school",
  Supervisor = "supervisor",
  UserInfo = "userinfo",
  Volunteer = "volunteer",

  ActivityImport = "activity-import",
  ActivityReviewImport = "activity-review-import",
  OrganizationImport = "organization-import",
  SupervisorImport = "supervisor-import",
  VolunteerImport = "volunteer-import",
}

export interface DocBase<TData> {
  type: DocType;
  version: number;
  timestamp: string;
  data: TData;
}

interface DocT<DT extends DocType, TData, V extends number = 1>
  extends DocBase<TData> {
  type: DT;
  version: V;
  timestamp: string;
  data: TData;
}

export type ActivityDocument = DocT<DocType.Activity, Activity>;
export type ActivityReviewDocument = DocT<
  DocType.ActivityReview,
  ActivityReview
>;
export type AdministratorDocument = DocT<DocType.Administrator, Administrator>;
export type CategoryDocument = DocT<DocType.Category, Category>;
export type CoordinatorDocument = DocT<DocType.Coordinator, Coordinator>;
export type MarkdownDocument = DocT<DocType.Markdown, Markdown>;
export type OrganizationDocument = DocT<DocType.Organization, Organization>;
export type SchoolDocument = DocT<DocType.School, School>;
export type SupervisorDocument = DocT<DocType.Supervisor, Supervisor>;
export type UserInfoDocument = DocT<DocType.UserInfo, UserInfo>;
export type VolunteerDocument = DocT<DocType.Volunteer, Volunteer>;

export type ActivityImportDocument = DocT<
  DocType.ActivityImport,
  ActivityImport
>;
export type OrganizationImportDocument = DocT<
  DocType.OrganizationImport,
  OrganizationImport
>;
export type SupervisorImportDocument = DocT<
  DocType.SupervisorImport,
  SupervisorImport
>;
export type VolunteerImportDocument = DocT<
  DocType.VolunteerImport,
  VolunteerImport
>;

export const uidGetter = <Data extends HasUid>(doc: DocBase<Data>) =>
  doc.data.uid;
export const nameGetter = <Data extends HasName>(doc: DocBase<Data>) =>
  doc.data.name;
export const nameComparer = <Doc extends HasName>() =>
  comparerByField<DocBase<Doc>, string>(nameGetter);

export const docData = <Doc extends unknown>(doc: DocBase<Doc> | null) =>
  doc && doc.data;

export type ByUid<T> = { [s: string]: T };

export function byUid<Doc extends DocBase<HasUid>>(docs: Doc[]): ByUid<Doc> {
  return docs.reduce((acc: ByUid<Doc>, x) => {
    acc[x.data.uid] = x;
    return acc;
  }, {});
}

export function mapDocUids<Doc extends DocBase<HasUid>>(items: Doc[]): Map<Uid, Doc> {
  return items.reduce((acc: Map<Uid, Doc>, x) => {
    acc.set(x.data.uid, x);
    return acc;
  }, new Map());
}

export function mapUids<Item extends HasUid>(items: Item[]): Map<Uid, Item> {
  return items.reduce((acc: Map<Uid, Item>, x) => {
    acc.set(x.uid, x);
    return acc;
  }, new Map());
}

// Document construction

function now(): string {
  return new Date().toISOString();
}

function v1doc<DT extends DocType, T>(dt: DT, data: T): DocT<DT, T> {
  return {
    type: dt,
    version: SCHEMA_V1,
    timestamp: now(),
    data,
  };
}

export const activityDoc = (data: Activity) => v1doc(DocType.Activity, data);
export const activityReviewDoc = (data: ActivityReview) =>
  v1doc(DocType.ActivityReview, data);
export const administratorDoc = (data: Administrator) =>
  v1doc(DocType.Administrator, data);
export const categoryDoc = (data: Category) => v1doc(DocType.Category, data);
export const coordinatorDoc = (data: Coordinator) =>
  v1doc(DocType.Coordinator, data);
export const markdownDoc = (data: Markdown) => v1doc(DocType.Markdown, data);
export const organizationDoc = (data: Organization) =>
  v1doc(DocType.Organization, data);
export const schoolDoc = (data: School) => v1doc(DocType.School, data);
export const supervisorDoc = (data: Supervisor) =>
  v1doc(DocType.Supervisor, data);
export const userInfoDoc = (data: UserInfo) => v1doc(DocType.UserInfo, data);
export const volunteerDoc = (data: Volunteer) => v1doc(DocType.Volunteer, data);

export const activityImportDoc = (data: ActivityImport) =>
  v1doc(DocType.ActivityImport, data);
export const organizationImportDoc = (data: OrganizationImport) =>
  v1doc(DocType.OrganizationImport, data);
export const supervisorImportDoc = (data: SupervisorImport) =>
  v1doc(DocType.SupervisorImport, data);
export const volunteerImportDoc = (data: VolunteerImport) =>
  v1doc(DocType.VolunteerImport, data);
